import Avatar from 'ext-ui/components/Avatar/Avatar'
import Pill from 'ext-ui/components/Pill/Pill'
import React, { ReactElement } from 'react'
import { ExtendCurrency } from 'src/enums'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'

import { visuallyHidden } from '../CardDetailHero.style'
import {
  creditMeterPercent,
  initialsFrom,
  toDisplayAmount,
} from '../cardDetailHeroFormat'
import { PhysicalCardManagementStatus } from '../cardDetailHeroTypes'
import CardNickname from '../CardNickname/CardNickname'
import SvgCard from '../icons/SvgCard'
import { statusPillColor } from '../StatusPill/StatusPill'
import { useCardDetailHeroData } from '../useCardDetailHeroData'
import {
  useIdentityStateText,
  useLimitsStateText,
} from '../useCardDetailHeroStateText'
import { useCardStatusLabel } from '../useCardStatusLabel'
import * as style from './CardSummary.style'

// The currency-aware `toDisplayAmount` (review finding #1) lives in
// cardDetailHeroFormat, imported above, so both hero variants share it.

type CardSummaryProps = {
  /**
   * The card whose identity and limits this summary reads. Supplied by the page
   * (features never resolve routing), and threaded down so the region reads the
   * same seam the shell does — React Query dedupes both observers.
   */
  creditCardId: string | undefined
  /**
   * Card status is owned by the hero shell — so the pill, the card-art overlay,
   * and the freeze button never diverge — and passed in, rather than read from
   * the seam here. `undefined` while identity loads, or when the backend status
   * has no card-management counterpart: better no pill than a wrong one.
   */
  status: PhysicalCardManagementStatus | undefined
  /**
   * Whether this user may rename the card (`canUpdateCreditCard`). Resolved by
   * the page alongside the card's other permissions and passed down, rather than
   * fetched here — the same reason `status` is a prop.
   */
  canUpdateCard: boolean
  /**
   * Whether this variant is the on-screen one. The full hero and the pinned
   * compact bar render the SAME group-state messages and are both always
   * mounted, so without this both would be live regions and a single failure
   * could be announced twice. The off-screen variant keeps the visible text and
   * drops only the live-region role.
   */
  announceStates: boolean
}

const CardSummary: React.FC<CardSummaryProps> = ({
  creditCardId,
  status,
  canUpdateCard,
  announceStates,
}): ReactElement => {
  const translate = useTranslate()
  const currencyFormatter = useCurrencyFormatter()
  const identityStateText = useIdentityStateText()
  const limitsStateText = useLimitsStateText()
  const statusLabel = useCardStatusLabel()
  const { card, cardState, limits, limitsState, renameCard, renameState } =
    useCardDetailHeroData(creditCardId)

  return (
    <div css={style.wrapper}>
      {card !== undefined ? (
        <div css={style.lockupColumn}>
          <div css={style.titleRow}>
            <div css={style.personGroup}>
              <Avatar
                size={28}
                fallback={
                  <span className="fallback-text">
                    {initialsFrom(card.cardholder.displayName)}
                  </span>
                }
              />
              <h2 css={style.cardholderName} data-testid="cardholder-name">
                {card.cardholder.displayName}
              </h2>
            </div>
            {/*
              Status and the masked last four joined into one tinted unit
              (Design VQA round 2, slice B). The last four is a permanent card
              fact, not a status signal, so it and the divider stay explicitly
              neutral in every state — see the `statusUnit` doc comment in
              CardSummary.style.ts.

              The leading glyph is the card glyph in EVERY state, matching the
              design prototype's V2 lockup — not `statusPillIcon`'s per-state
              snowflake/padlock, which is why an ACTIVE card rendered no glyph
              at all (design QA). It sits outside the status guard because it
              identifies the card, like the last four, rather than reporting a
              state. Colour is still never the only signal: the status label
              below is always rendered alongside the tint. StatusPill keeps its
              per-state glyphs for the pinned compact bar.
            */}
            <span
              css={style.statusUnit(status && statusPillColor(status))}
              data-testid="card-status-unit"
            >
              <SvgCard
                css={style.statusGlyph}
                data-testid="card-status-glyph"
              />
              {status !== undefined && (
                <>
                  <span css={style.statusLabel}>{statusLabel(status)}</span>
                  <span
                    css={style.statusDivider}
                    aria-hidden="true"
                    data-testid="card-status-divider"
                  />
                </>
              )}
              <span css={style.lastFour} data-testid="card-last-four">
                <span aria-hidden="true">{'••  '}</span>
                <span css={visuallyHidden}>{translate('Card ending in ')}</span>
                {card.last4}
              </span>
            </span>
          </div>
          <div css={style.supportingRow}>
            <span css={style.nicknameRow}>
              <CardNickname
                nickname={card.nickname}
                canRename={canUpdateCard}
                onRename={renameCard}
                isSaving={renameState.isPending}
                isError={renameState.isError}
                pencilSize={14}
              />
            </span>
            <Pill size="small">{translate('Physical')}</Pill>
          </div>
        </div>
      ) : (
        // Identity is its own group, so it announces its own state — a limits
        // failure below can never blank this row, and vice versa.
        <p
          css={style.metaText}
          role={
            announceStates
              ? cardState === 'loading'
                ? 'status'
                : 'alert'
              : undefined
          }
          data-testid="card-summary-identity-state"
        >
          {identityStateText(cardState)}
        </p>
      )}
      <div css={style.creditBlock}>
        {limits !== undefined ? (
          <>
            <span css={style.balanceLabel}>{translate('Card balance')}</span>
            <span css={style.creditAmount} data-testid="card-balance-amount">
              {currencyFormatter({
                // No maximumFractionDigits override — the balance carries
                // cents ("$1,284.50"), unlike the no-cents remaining figure
                // below.
                amount: toDisplayAmount(limits.cardFinancials.balance),
                currency: limits.cardFinancials.balance
                  .currency as ExtendCurrency,
              })}
            </span>
            {/*
              No "Resets <date>" element: there is no reset-date field
              anywhere in this codebase (deliberately not reusing the
              statement-close date — see the design VQA's own note on why
              that would assert an unmade product decision). The row
              degrades to "Available credit" alone, which is the specified
              behaviour, not a placeholder for a missing feature.
            */}
            <div css={style.creditRow}>
              <span css={style.creditRowLabel}>
                {translate('Available credit')}
              </span>
            </div>
            <div css={style.meterTrack} aria-hidden="true">
              <div
                css={style.meterFill}
                style={{
                  width: `${creditMeterPercent(
                    limits.permanentCardSpendControl.limit,
                    limits.cardFinancials.availableSpend,
                  )}%`,
                }}
                data-testid="card-credit-meter-fill"
              />
            </div>
            <span css={style.remaining} data-testid="card-credit-remaining">
              {translate('${amount} remaining', {
                amount: currencyFormatter({
                  // Not clamped at zero: an over-limit card really does have a
                  // negative available spend, and the contract preserves it.
                  amount: toDisplayAmount(limits.cardFinancials.availableSpend),
                  currency: limits.cardFinancials.availableSpend
                    .currency as ExtendCurrency,
                  maximumFractionDigits: 0,
                }),
              })}
            </span>
          </>
        ) : (
          <span
            css={style.creditLimit}
            role={
              announceStates
                ? limitsState === 'loading'
                  ? 'status'
                  : 'alert'
                : undefined
            }
            data-testid="card-summary-limits-state"
          >
            {limitsStateText(limitsState)}
          </span>
        )}
      </div>
    </div>
  )
}

export default CardSummary
